import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import Pagination from "../components/Pagination";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import api from "../api/api";
import { useToast } from "../components/ToastNotification";
import { useSiteConfig } from "../utils/siteConfig";
import { getProductCardImages } from "../utils/imageUtils";
import { SkeletonProductCard } from "../components/SkeletonLoaders";
import { AnimatedSection } from "../components/animations";
import { sectionHeading, staggerContainer, staggerItem, productCardReveal, prefersReducedMotion } from "../components/animations";
import {
  FiSearch,
  FiFilter,
  FiX,
  FiChevronDown,
} from "react-icons/fi";

// Dual-thumb price range slider
const PriceRangeSlider = ({ min, max, value, onChange }) => {
  const [low, high] = value;
  const range = Math.max(1, max - min);
  const lowPct = Math.min(100, Math.max(0, ((low - min) / range) * 100));
  const highPct = Math.min(100, Math.max(0, ((high - min) / range) * 100));

  return (
    <div className="px-1">
      <div className="mb-4 flex items-center justify-between text-sm font-medium" style={{ color: 'var(--text)' }}>
        <span>Rs. {low.toLocaleString()}</span>
        <span>Rs. {high.toLocaleString()}</span>
      </div>
      <div className="relative h-1.5 rounded-full" style={{ backgroundColor: 'var(--border)' }}>
        <div
          className="absolute h-1.5 rounded-full"
          style={{ backgroundColor: 'var(--primary)', left: `${lowPct}%`, right: `${100 - highPct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={low}
          onChange={(e) =>
            onChange([Math.min(Number(e.target.value), high - 1), high])
          }
          className="pointer-events-none absolute left-0 top-1/2 h-1.5 w-full -translate-y-1/2 appearance-none bg-transparent
            [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-[var(--primary)] [&::-webkit-slider-thumb]:bg-[var(--card-bg)] [&::-webkit-slider-thumb]:shadow
            [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[var(--primary)] [&::-moz-range-thumb]:bg-[var(--card-bg)]"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={high}
          onChange={(e) =>
            onChange([low, Math.max(Number(e.target.value), low + 1)])
          }
          className="pointer-events-none absolute left-0 top-1/2 h-1.5 w-full -translate-y-1/2 appearance-none bg-transparent
            [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-[var(--primary)] [&::-webkit-slider-thumb]:bg-[var(--card-bg)] [&::-webkit-slider-thumb]:shadow
            [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[var(--primary)] [&::-moz-range-thumb]:bg-[var(--card-bg)]"
        />
      </div>

      {/* Manual min/max inputs */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
            Min
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-70" style={{ color: 'var(--text-secondary)' }}>
              Rs.
            </span>
            <input
              type="number"
              min={min}
              max={high}
              value={low}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isNaN(v)) return;
                onChange([Math.min(Math.max(v, min), high - 1), high]);
              }}
              style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text)', borderColor: 'var(--input-border)' }}
              className="w-full rounded-lg border py-2 pl-7 pr-2 text-sm outline-none transition focus:ring-4 focus:ring-[var(--primary)]/10"
            />
          </div>
        </div>
        <span className="mt-5 opacity-70" style={{ color: 'var(--text-secondary)' }}>–</span>
        <div className="flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
            Max
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-70" style={{ color: 'var(--text-secondary)' }}>
              Rs.
            </span>
            <input
              type="number"
              min={low}
              max={high}
              value={high}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isNaN(v)) return;
                onChange([low, Math.max(Math.min(v, max), low + 1)]);
              }}
              style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text)', borderColor: 'var(--input-border)' }}
              className="w-full rounded-lg border py-2 pl-7 pr-2 text-sm outline-none transition focus:ring-4 focus:ring-[var(--primary)]/10"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FiltersContent = ({
  categories,
  selectedCategory,
  onSelectCategory,
  subcategoryOptions = [],
  selectedSubcategory,
  onSelectSubcategory,
  subcategoriesByCategory = {},
  onSelectCategoryAndSub,
  search,
  onSearch,
  priceBounds,
  priceValue,
  onPriceChange,
  sort,
  onSortChange,
  onClearAll,
}) => {
  const [openFlyout, setOpenFlyout] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const closeFlyout = () => {
    setOpenFlyout(null);
    setAnchorRect(null);
  };

  return (
    <div className="space-y-7">
      {/* Search */}
      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
          Search
        </label>
        <div className="relative">
          <FiSearch
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search products..."
            style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text)', borderColor: 'var(--input-border)' }}
            className="w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm outline-none transition focus:ring-4 focus:ring-[var(--primary)]/10"
          />
        </div>
      </div>

      {/* Categories */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
          Categories
        </h3>
        <ul className="space-y-1">
          <li>
            <button
              type="button"
              onClick={() => onSelectCategory("")}
              style={{ backgroundColor: selectedCategory === "" ? 'var(--primary)' : 'transparent', color: selectedCategory === "" ? 'var(--btn-primary-text, #fff)' : 'var(--text)' }}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition ${selectedCategory !== "" && "hover:bg-gray-100"}`}
            >
              All Products
              {selectedCategory === "" && <FiChevronDown size={14} className="rotate-90" />}
            </button>
          </li>
          {categories.map((cat) => {
            const name = cat.name || cat;
            const active = selectedCategory === name;
            const subs = subcategoriesByCategory[name] || [];
            return (
              <li key={name} className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    if (subs.length === 0) {
                      onSelectCategory(name);
                      return;
                    }
                    const rect = e.currentTarget.getBoundingClientRect();
                    setOpenFlyout((prev) => (prev === name ? null : name));
                    setAnchorRect((prev) =>
                      prev && openFlyout === name ? null : rect
                    );
                  }}
                  style={{ backgroundColor: active ? 'var(--primary)' : 'transparent', color: active ? 'var(--btn-primary-text, #fff)' : 'var(--text)' }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition ${!active && "hover:bg-gray-100"}`}
                >
                  <span>{name}</span>
                  {subs.length > 0 ? (
                    <FiChevronDown
                      size={14}
                      className={`transition ${openFlyout === name ? "rotate-180" : ""
                        } ${active ? "text-white" : "text-gray-400"}`}
                    />
                  ) : (
                    active && <FiChevronDown size={14} className="rotate-90" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {/* Subcategory popup rendered in a portal */}
        {openFlyout && anchorRect && subcategoriesByCategory[openFlyout] && createPortal(
          (() => {
            const subs = subcategoriesByCategory[openFlyout] || [];
            const activeCat = selectedCategory === openFlyout;
            const left = Math.min(
              anchorRect.right + 8,
              (typeof window !== "undefined" ? window.innerWidth : 9999) - 220
            );
            return (
              <>
                <div
                  className="fixed inset-0 z-[999]"
                  onClick={closeFlyout}
                  aria-hidden="true"
                />
                <ul
                  className="fixed z-[1000] w-52 rounded-xl border p-2 shadow-xl transition-colors duration-300"
                  style={{ top: anchorRect.top, left, backgroundColor: 'var(--card-bg)', borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectCategoryAndSub(openFlyout, "");
                        closeFlyout();
                      }}
                      style={{ color: 'var(--primary)' }}
                      className="mb-1 block w-full rounded-lg px-3 py-1.5 text-left text-sm font-semibold transition hover:bg-[var(--primary)]/10"
                    >
                      All {openFlyout}
                    </button>
                  </li>
                  {subs.map((sub) => {
                    const subActive = activeCat && selectedSubcategory === sub;
                    return (
                      <li key={sub}>
                        <button
                          type="button"
                          onClick={() => {
                            onSelectCategoryAndSub(openFlyout, sub);
                            closeFlyout();
                          }}
                          style={{ backgroundColor: subActive ? 'var(--primary)/10' : 'transparent', color: subActive ? 'var(--primary)' : 'var(--text)' }}
                          className={`block w-full rounded-lg px-3 py-1.5 text-left text-sm transition ${!subActive && "hover:bg-gray-100"}`}
                        >
                          {sub}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            );
          })(),
          document.body
        )}
      </div>

      {/* Price Range */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
          Price Range
        </h3>
        <PriceRangeSlider
          min={priceBounds[0]}
          max={priceBounds[1]}
          value={priceValue}
          onChange={onPriceChange}
        />
      </div>

      {/* Sort */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide opacity-70" style={{ color: 'var(--text-secondary)' }}>
          Sort By
        </h3>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text)', borderColor: 'var(--input-border)' }}
          className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:ring-4 focus:ring-[var(--primary)]/10"
        >
          <option value="default">Featured</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="rating">Top Rated</option>
        </select>
      </div>

      <button
        type="button"
        onClick={onClearAll}
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}
        className="w-full rounded-xl border py-2.5 text-sm font-semibold transition hover:opacity-80"
      >
        Clear All Filters
      </button>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { siteUrl, siteName } = useSiteConfig();
  const productsSectionRef = useRef(null);

  // Derived filter params directly from searchParams
  const categoryParam = searchParams.get("category") || "";
  const subcategoryParam = searchParams.get("subcategory") || "";
  const urlSearchParam = searchParams.get("search") || "";
  const pageParam = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  // Controlled UI states
  const [search, setSearch] = useState(urlSearchParam);
  const [priceBounds] = useState([0, 200000]);
  const [priceValue, setPriceValue] = useState([0, 200000]);
  const [sort, setSort] = useState("default");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [subcategoriesByCategoryMap, setSubcategoriesByCategoryMap] = useState({});
  const [paginationInfo, setPaginationInfo] = useState({ total: 0, page: 1, pages: 1, limit: 20 });
  const lastTrackedSearchRef = useRef("");

  const isSearchResultsPage = useMemo(() => {
    return Boolean(urlSearchParam && urlSearchParam.trim().length > 0);
  }, [urlSearchParam]);

  // Keep search input state in sync with URL search param changes
  useEffect(() => {
    setSearch(urlSearchParam);
  }, [urlSearchParam]);

  const updateSearchParams = useCallback(
    (updates, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") {
              next.set(k, v);
            } else {
              next.delete(k);
            }
          });
          if (resetPage) {
            next.delete("page");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectCategory = useCallback(
    (cat) => {
      updateSearchParams({ category: cat, subcategory: "" }, true);
      setMobileFiltersOpen(false);
    },
    [updateSearchParams]
  );

  const handleSelectSubcategory = useCallback(
    (sub) => {
      updateSearchParams({ subcategory: sub }, true);
      setMobileFiltersOpen(false);
    },
    [updateSearchParams]
  );

  const handleSelectCategoryAndSub = useCallback(
    (cat, sub) => {
      updateSearchParams({ category: cat, subcategory: sub || "" }, true);
      setMobileFiltersOpen(false);
    },
    [updateSearchParams]
  );

  const handleSearchChange = useCallback(
    (term) => {
      setSearch(term);
      updateSearchParams({ search: term }, true);
    },
    [updateSearchParams]
  );

  const handlePriceChange = useCallback(
    (val) => {
      setPriceValue(val);
    },
    []
  );

  const handleSortChange = useCallback(
    (s) => {
      setSort(s);
    },
    []
  );

  const handleClearAll = () => {
    setSearch("");
    setPriceValue([0, 200000]);
    setSort("default");
    setSearchParams({}, { replace: true });
    setMobileFiltersOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage === pageParam) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage <= 1) next.delete("page");
        else next.set("page", newPage);
        return next;
      },
      { replace: true }
    );
    if (productsSectionRef.current) {
      productsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Fetch site content categories once on mount
  useEffect(() => {
    api.get("/site-content")
      .then((siteRes) => {
        if (siteRes?.data?.success && Array.isArray(siteRes.data.data?.categories)) {
          setCategories(siteRes.data.data.categories.filter((c) => c.name));
        }
      })
      .catch(() => {});
  }, []);

  const priceLow = priceValue[0];
  const priceHigh = priceValue[1];

  // Main data fetch effect: server-side pagination with query parameters
  useEffect(() => {
    let isCancelled = false;

    const fetchProductsData = async () => {
      try {
        setLoading(true);
        setError("");

        const params = {
          page: pageParam,
          limit: 20,
        };

        if (categoryParam) params.category = categoryParam;
        if (subcategoryParam) params.subcategory = subcategoryParam;
        if (urlSearchParam.trim()) params.search = urlSearchParam.trim();
        if (priceLow > priceBounds[0]) params.minPrice = priceLow;
        if (priceHigh < priceBounds[1] && priceHigh > 0) params.maxPrice = priceHigh;
        if (sort !== "default") params.sort = sort;

        const res = await api.get("/products", { params });

        if (isCancelled) return;

        if (res.data?.success) {
          const list = res.data.data || [];
          const pagination = res.data.pagination || { total: list.length, page: pageParam, pages: 1, limit: 20 };
          setProducts(list);
          setPaginationInfo(pagination);

          const currentSearchTerm = urlSearchParam.trim();
          if (currentSearchTerm && window.fbq && lastTrackedSearchRef.current !== currentSearchTerm) {
            lastTrackedSearchRef.current = currentSearchTerm;
            window.fbq("track", "Search", {
              search_string: currentSearchTerm
            });
          }

          // Accumulate subcategories for sidebar flyout options
          setSubcategoriesByCategoryMap((prev) => {
            const next = { ...prev };
            list.forEach((p) => {
              const cat = Array.isArray(p.category) ? p.category[0] : p.category;
              if (cat && p.subcategory) {
                if (!next[cat]) next[cat] = new Set();
                else if (!(next[cat] instanceof Set)) next[cat] = new Set(next[cat]);
                next[cat].add(p.subcategory);
              }
            });
            const formatted = {};
            Object.keys(next).forEach((k) => {
              formatted[k] = Array.from(next[k]).sort();
            });
            return formatted;
          });
        }
      } catch (err) {
        if (!isCancelled) {
          const msg = err?.response?.data?.message || err.message || "Failed to load products.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchProductsData();

    return () => {
      isCancelled = true;
    };
  }, [pageParam, categoryParam, subcategoryParam, urlSearchParam, priceLow, priceHigh, priceBounds, sort, toast]);

  const breadcrumbs = useMemo(() => {
    const crumbs = [{ name: "Home", url: `${siteUrl}/` }];
    if (categoryParam) {
      crumbs.push({ name: "Products", url: `${siteUrl}/products` });
      crumbs.push({ name: categoryParam, url: `${siteUrl}/products?category=${encodeURIComponent(categoryParam)}` });
      if (subcategoryParam) {
        crumbs.push({ name: subcategoryParam, url: `${siteUrl}/products?category=${encodeURIComponent(categoryParam)}&subcategory=${encodeURIComponent(subcategoryParam)}` });
      }
    } else {
      crumbs.push({ name: "Products", url: `${siteUrl}/products` });
    }
    return crumbs;
  }, [categoryParam, subcategoryParam, siteUrl]);

  const subcategoryOptions = useMemo(() => {
    if (!categoryParam) return [];
    return subcategoriesByCategoryMap[categoryParam] || [];
  }, [subcategoriesByCategoryMap, categoryParam]);

  const productSkeletons = useMemo(() => [...Array(6)], []);

  return (
    <div ref={productsSectionRef} className="mx-auto max-w-full py-6 px-8 sm:px-6 lg:px-32">
      <Breadcrumb crumbs={breadcrumbs.map(b => ({ name: b.name, path: b.url.replace(siteUrl, '') }))} />
      <SEO
        title={`Products${categoryParam ? ` - ${categoryParam}` : ""} - ${siteName}`}
        description="Browse our full range of Premium Office Chairs, Gaming Chairs, Bar Stools & Office Furniture Shop in Lahore."
        canonicalUrl={`${siteUrl}/products${categoryParam ? `?category=${encodeURIComponent(categoryParam)}` : ''}`}
        products={products}
        breadcrumbs={breadcrumbs}
        category={categoryParam}
      />

      <AnimatedSection>
        {/* Header / Mobile filter toggle */}
        <motion.div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" variants={sectionHeading}>
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl" style={{ color: 'var(--text)' }}>
              {isSearchResultsPage ? `Search Results` : (subcategoryParam || categoryParam || "All Products")}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {loading
                ? "Loading..."
                : `${paginationInfo.total} product${paginationInfo.total === 1 ? "" : "s"} found${
                    paginationInfo.pages > 1 ? ` (Page ${paginationInfo.page} of ${paginationInfo.pages})` : ""
                  }`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-[#12131A] transition hover:bg-gray-50 lg:hidden"
          >
            <FiFilter size={16} />
            Filters
          </button>
        </motion.div>
      </AnimatedSection>

      <div className="mt-6 flex gap-8">
        {/* Desktop Sidebar */}
        <AnimatedSection direction="right" className="hidden w-64 shrink-0 lg:block">
          <aside className="w-full">
            <motion.div variants={sectionHeading} className="sticky top-6 rounded-2xl border p-5 shadow-xs transition-colors duration-300" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border)', color: 'var(--text)' }}>
              <FiltersContent
                categories={categories}
                selectedCategory={categoryParam}
                onSelectCategory={handleSelectCategory}
                subcategoryOptions={subcategoryOptions}
                selectedSubcategory={subcategoryParam}
                onSelectSubcategory={handleSelectSubcategory}
                subcategoriesByCategory={subcategoriesByCategoryMap}
                onSelectCategoryAndSub={handleSelectCategoryAndSub}
                search={search}
                onSearch={handleSearchChange}
                priceBounds={priceBounds}
                priceValue={priceValue}
                onPriceChange={handlePriceChange}
                sort={sort}
                onSortChange={handleSortChange}
                onClearAll={handleClearAll}
              />
            </motion.div>
          </aside>
        </AnimatedSection>

        {/* Product Grid */}
        <main className="min-w-0 flex-1">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-4">
                  {productSkeletons.map((_, i) => (
                    <SkeletonProductCard key={i} />
                  ))}
                </div>
              </motion.div>
            ) : error ? (
              <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-20 text-center text-red-600">{error}</motion.div>
            ) : products.length === 0 ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-20 text-center text-gray-500">
                {isSearchResultsPage
                  ? <>No products match your search.<div className="mt-4"><button type="button" onClick={handleClearAll} className="rounded-xl bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">Clear Search</button></div></>
                  : <>No products match your filters.<div className="mt-4"><button type="button" onClick={handleClearAll} className="rounded-xl bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">Clear Filters</button></div></>}
              </motion.div>
            ) : (
              <div className="space-y-8">
                <motion.div key={`products-page-${pageParam}`} variants={staggerContainer} initial="initial" animate="animate" 
                className="grid grid-cols-1 justify-items-center sm:justify-items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-4">
                  {products.map((product) => {
                    const { primaryImage, hoverImage } = getProductCardImages(product);

                    return (
                      <motion.div key={product._id} className="w-full flex justify-center" variants={prefersReducedMotion() ? staggerItem : productCardReveal}>
                        <ProductCard
                          to={product.slug ? `/products/${product.slug}` : `/products/${product.slug}`}
                          image={primaryImage || product.imageUrl || "https://images.unsplash.com/photo-1505843490701-5be5d6f48db6?w=500"}
                          hoverImage={hoverImage}
                          name={product.name}
                          price={product.price}
                          description={product.shortDescription || product.description || product.detail || "No description available."}
                          rating={product.avgRating || 0}
                          reviews={product.totalReviews || 0}
                          category={product.category}
                          isCustomizable={product.isCustomizable === true}
                          product={product}
                        />
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Professional Pagination Controls */}
                <Pagination
                  currentPage={paginationInfo.page}
                  totalPages={paginationInfo.pages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[100] flex lg:hidden">
          <div
            className="absolute inset-0 bg-[#0F1320]/60 backdrop-blur-sm"
            onClick={() => setMobileFiltersOpen(false)}
            aria-hidden="true"
          />
          <div className="relative ml-auto flex h-full w-[85%] max-w-sm flex-col shadow-xl transition-colors duration-300" style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}>
            <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'var(--border)' }}>
              <h2 className="text-base font-semibold" style={{ color: 'var(--text)' }}>Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close filters"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <FiltersContent
                categories={categories}
                selectedCategory={categoryParam}
                onSelectCategory={handleSelectCategory}
                subcategoryOptions={subcategoryOptions}
                selectedSubcategory={subcategoryParam}
                onSelectSubcategory={handleSelectSubcategory}
                subcategoriesByCategory={subcategoriesByCategoryMap}
                onSelectCategoryAndSub={handleSelectCategoryAndSub}
                search={search}
                onSearch={handleSearchChange}
                priceBounds={priceBounds}
                priceValue={priceValue}
                onPriceChange={handlePriceChange}
                sort={sort}
                onSortChange={handleSortChange}
                onClearAll={handleClearAll}
              />
            </div>
            <div className="border-t border-gray-100 p-4">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                style={{ backgroundColor: 'var(--primary)', color: 'var(--btn-primary-text, #fff)' }}
                className="w-full rounded-xl py-3 text-sm font-semibold transition hover:opacity-90"
              >
                Show {paginationInfo.total} Result{paginationInfo.total === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;